import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";

import PrisaComponent from "./components/HaklaiPrisaComponent";
import { getAmountOptions } from "../../../../services/utils/utils";
import { haklaiLockableSectionStyles } from "../shared/table/haklaiTableShared";
import HaklaiSectionAccordion from "../shared/ui/HaklaiSectionAccordion";
import HaklaiLockNotice from "../shared/ui/HaklaiLockNotice";

/**
 * Owns the Haklai prisa section wrapper.
 *
 * Responsibilities:
 * - provides Haklai lock/accordion chrome around the prisa editor
 * - wires prisa validation state into the support component
 * - keeps generic prisa field rendering under `components/`
 */

const useStyles = makeStyles(() => haklaiLockableSectionStyles);
const HAKLAI_PRISA_AMOUNT_OPTIONS = getAmountOptions(6);

const HaklaiPrisaSection = ({
  prisa,
  soldPropertyDate,
  maxYears,
  minYears,
  handlePrisaOnChange,
  handlePrisaCreditPointsApply,
  locked = false,
  lockNotice = null,
  expanded = undefined,
  onToggleExpanded = undefined,
  fieldErrors = {},
  onFieldBlur
}) => {
  const classes = useStyles();
  const contentRef = useRef(null);

  useEffect(() => {
    if (!locked || !contentRef.current) return;
    const activeElement = document.activeElement;
    if (activeElement && contentRef.current.contains(activeElement)) {
      activeElement.blur();
    }
  }, [locked]);

  const bannerNode = (
    <HaklaiLockNotice
      classes={classes}
      message={locked ? lockNotice?.message : ""}
    />
  );

  return (
    <PrisaComponent
      {...prisa}
      shouldTryPrisaShown
      isClientsNumDisabled={false}
      soldPropertyDate={soldPropertyDate}
      maxYears={maxYears}
      minYears={minYears}
      options={HAKLAI_PRISA_AMOUNT_OPTIONS}
      handlePrisaOnChange={handlePrisaOnChange}
      handlePrisaCreditPointsApply={handlePrisaCreditPointsApply}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      fieldErrors={fieldErrors}
      onFieldBlur={onFieldBlur}
      useHaklaiValidation
      beforeContent={bannerNode}
      contentWrapperClassName={locked ? classes.lockedSectionContent : undefined}
      contentWrapperRef={contentRef}
      contentInert={locked}
      // Use the Haklai-native accordion shell so the expand arrow aligns
      // with the other Haklai sections (PreCalc/PropertyParts/Expenses).
      // The default `CustomAcordion` shell is MUI-flavoured and its
      // arrow sits ~8px further inset, which read as "misaligned" next
      // to the other sections.
      AccordionComponent={HaklaiSectionAccordion}
    />
  );
};

export default React.memo(HaklaiPrisaSection);
